import { createContext, useContext, useMemo } from 'react';

import type { PropsWithChildren, ReactElement } from 'react';

export type Environment = 'development' | 'production';

interface EnvironmentContextValue {
  environment: Environment;
}

// The Dev/Prod "Environment" scope has been retired from the dashboard (the
// global top-bar toggle and the standalone promote surface are gone) — the
// app is now single-scope, pinned to Production. This provider is kept as a
// thin passthrough so the ~70 call sites that pass `environment` through to a
// GraphQL query variable (via `toGqlEnvironment`) keep compiling unchanged;
// none of them ever see anything but 'production' again. Removing the
// underlying `Environment` GraphQL enum + DB columns is a later phase owned
// elsewhere — this component intentionally still exists for that reason.
const PINNED_ENVIRONMENT: Environment = 'production';
const CONTEXT_VALUE: EnvironmentContextValue = { environment: PINNED_ENVIRONMENT };

const EnvironmentContext = createContext<EnvironmentContextValue | undefined>(undefined);

export function EnvironmentProvider({ children }: PropsWithChildren): ReactElement {
  const value = useMemo<EnvironmentContextValue>(() => CONTEXT_VALUE, []);
  return <EnvironmentContext.Provider value={value}>{children}</EnvironmentContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- co-located hook by design
export function useEnvironment(): EnvironmentContextValue {
  const ctx = useContext(EnvironmentContext);
  if (!ctx) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return ctx;
}
